import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { UserOnEntity, BidDecision, BidDecisionGroup, BidDecisionGroupPermissionEnum, CommentEnum, DecisionActionEnum, DecisionEnum, TenderState } from "@/graphql/__generated__/graphql";
import { useCommentOnDecisionGroupMutationHook, useSubmitDecisionMutationHook } from "@/hooks";
import { onInvalid } from "@/utils/form";
import { zodResolver } from "@hookform/resolvers/zod";
import { useEffect, useState, useRef, JSX, RefObject } from "react";
import { Resolver, SubmitHandler, useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { useParams } from "react-router-dom";
// import { z } from "zod";
import { TFunction } from "i18next";
import { DropdownSelect } from "@/components/shared";
import { useFetchBidDecisionsByGroupPolling } from "@/hooks/queries";
import { ChatMentionsPopup } from "@/components/shared/mentions";
import { handleOnKeyDown } from "@/utils/helpers";
import { handleMentionDetection, transformMessageForSubmission, filterUsersByMention } from "@/components/shared/mentions/utils";
import { TriangleAlert } from "lucide-react";
import DecisionConfirmationAlertDialog from "./DecisionConfirmationAlertDialog";
import { CircleLoader } from "@/components/shared/loaders";
import { cn } from "@/lib/utils";
import * as z from "zod"; // v4
import { getAwaitingOrganizationName, isSkipOption, prefixSkipComment, SKIP_APPROVE, SKIP_BID, SKIP_FLIP, SKIP_NOBID } from "../../utils/decisionSkip";

interface Props {
  bidDecisions: BidDecision[];
  bidDecisionGroup: BidDecisionGroup;
  tenderState: TenderState;
  tenderUsers: UserOnEntity[];
  hasFinalDecision: boolean;
  userHasPermission: boolean;
};

const createBidDecisionSchema = (t: TFunction, currentDecision: DecisionEnum | null) => {
  return z
    .object({
      action: z
        .preprocess(
          (val) => (val === "" || val === null ? undefined : val),
          z.enum([DecisionActionEnum.Approve, DecisionActionEnum.Flip, DecisionActionEnum.Comment]).optional()
        ),
      decision: z
        .preprocess(
          (val) => (val === "" || val === null ? undefined : val),
          z.enum([DecisionEnum.Bid, DecisionEnum.Nobid]).optional()
        ),
      comment: z.string().optional().transform(val => val?.trim()),
      decideAheadOfTurn: z.boolean().optional(),
    })
    .check((ctx) => {
      // If action is not Flip, comment is required and must be non-empty
      if (ctx.value.action === DecisionActionEnum.Flip || ctx.value.action === DecisionActionEnum.Comment) {
        const comment = ctx.value.comment?.trim();
        if (!comment) {
          ctx.issues.push({
            path: ["comment"],
            message: t("chatinputbar.commentrequired"),
            code: "custom",
            input: ctx.value.comment,
          });
        }
      }

      // Comment is required in these cases:
      // 1. When action is Comment
      // 2. When action is Flip
      // 3. When action is Approve and currentDecision is NOT Bid
      const isCommentRequired =
        ctx.value.action === DecisionActionEnum.Comment ||
        ctx.value.action === DecisionActionEnum.Flip ||
        (ctx.value.action === DecisionActionEnum.Approve && currentDecision !== DecisionEnum.Bid) ||
        Boolean(ctx.value.decideAheadOfTurn);

      if (isCommentRequired) {
        const comment = ctx.value.comment?.trim();
        if (!comment) {
          ctx.issues.push({
            path: ["comment"],
            message: t("chatinputbar.commentrequired"),
            code: "custom",
            input: ctx.value.comment,
          });
        }
      }
    });
};

const BidDecisionDefaultValues = {
  action: DecisionActionEnum.Comment as any,
  decision: "" as any,
  comment: "",
  decideAheadOfTurn: false,
};

/**
 * @component ChatInputBar
 * @description Renders an input bar for users to submit their tender decisions.
 *
 * @param {Props} props - The properties for the component.
 *
 * @returns {JSX.Element} The rendered ChatInputBar component.
 */
const ChatInputBar = ({ bidDecisions, bidDecisionGroup, tenderState, userHasPermission, tenderUsers }: Props): JSX.Element => {

  // Hooks
  const { t } = useTranslation(["components", "common"]);
  const currentLanguage = localStorage.getItem("i18nextLng");
  const { bidDecisionGroupID, tenderID } = useParams<{ bidDecisionGroupID: string, tenderID: string }>();

  // States
  const [decisionConfirmationOpen, setDecisionConfirmationOpen] = useState<boolean>(false);
  const [dropdownValue, setDropdownValue] = useState<string>("");

  const currentDecision = bidDecisionGroup.currentDecision as DecisionEnum;
  const BidDecisionSchema = createBidDecisionSchema(t, currentDecision);
  type TBidDecisionSchema = z.infer<typeof BidDecisionSchema>;
  const form = useForm<TBidDecisionSchema>({
    resolver: zodResolver(BidDecisionSchema) as Resolver<TBidDecisionSchema>,
    defaultValues: BidDecisionDefaultValues,
    mode: "onChange",
  });

  // States
  const [mentionQuery, setMentionQuery] = useState<string | null>(null);
  const commentInputRef = useRef<HTMLInputElement | null>(null);

  // Watch the form values
  const actionValue = form.watch("action");
  const decisionValue = form.watch("decision");
  const commentValue = form.watch("comment");

  // Resets the form when the bidDecisionGroupId changes
  useEffect(() => {
    form.reset(BidDecisionDefaultValues);
    setDropdownValue("");
  }, [bidDecisionGroupID]);

  // Mutation hooks
  const { submitDecisionMutation, loading: submitLoading } = useSubmitDecisionMutationHook();
  const { commentOnDecisionGroupMutation, loading: commentLoading } = useCommentOnDecisionGroupMutationHook();
  const { useRefetch } = useFetchBidDecisionsByGroupPolling(bidDecisionGroupID as string, tenderID as string);

  // Constants
  // Filter out Comment actions
  const hasBidDecisions = bidDecisions?.filter(decision => decision.action !== DecisionActionEnum.Comment).length > 0;
  const hasFinalDecision = bidDecisionGroup.awaitingOrganizationId === null;
  const canDecide = bidDecisionGroup?.permissions?.includes(BidDecisionGroupPermissionEnum.Decide);
  const canDecideAheadOfTurn = bidDecisionGroup?.permissions?.includes(BidDecisionGroupPermissionEnum.DecideAheadOfTurn);
  const canComment = bidDecisionGroup?.permissions?.includes(BidDecisionGroupPermissionEnum.Comment);
  const awaitingOrganizationName = getAwaitingOrganizationName(
    bidDecisionGroup?.participantsOnDecisionGroup,
    bidDecisionGroup?.awaitingOrganizationId
  );
  const skipOrganizationLabel = awaitingOrganizationName ?? t("chatinputbar.waitingorganization");

  const commentOption = canComment ? { label: t("common:comment"), value: DecisionActionEnum.Comment } : null;

  const options = {
    initial: [
      ...(canDecide ? [
        { label: t("common:bid"), value: DecisionEnum.Bid },
        { label: t("common:nobid"), value: DecisionEnum.Nobid },
      ] : []),
      ...(canDecideAheadOfTurn ? [
        { label: t("chatinputbar.skipbid", { organizationName: skipOrganizationLabel }), value: SKIP_BID },
        { label: t("chatinputbar.skipnobid", { organizationName: skipOrganizationLabel }), value: SKIP_NOBID },
      ] : []),
      ...(commentOption ? [commentOption] : []),
    ],
    pending: [
      ...(canDecide ? [
        {
          label: currentDecision === DecisionEnum.Bid ? t("common:nobid") : t("common:bid"),
          value: DecisionActionEnum.Flip
        },
        {
          label: currentDecision === DecisionEnum.Bid ? t("common:bid") : t("common:nobid"),
          value: DecisionActionEnum.Approve
        },
      ] : []),
      ...(canDecideAheadOfTurn ? [
        {
          label: t(
            currentDecision === DecisionEnum.Bid ? "chatinputbar.skipnobid" : "chatinputbar.skipbid",
            { organizationName: skipOrganizationLabel }
          ),
          value: SKIP_FLIP
        },
        {
          label: t(
            currentDecision === DecisionEnum.Bid ? "chatinputbar.skipbid" : "chatinputbar.skipnobid",
            { organizationName: skipOrganizationLabel }
          ),
          value: SKIP_APPROVE
        },
      ] : []),
      ...(commentOption ? [commentOption] : []),
    ],
  };

  /**
   * Handles form submission for bid decisions or comments
   *
   * @param {TBidDecisionSchema} data - Form data containing action, decision, and comment
   * @returns {Promise<void>} - A promise that resolves when the submission is complete
   *
   * @description This function processes the form submission for bid decisions or comments.
   * It transforms @mentions in comments to the backend-expected format, determines the appropriate
   * action and decision based on context (initial decision vs. approving/flipping existing decisions),
   * submits either a comment or decision mutation based on the action type, and handles any errors
   * that may occur during submission. After successful submission, the form state is reset.
   */
  const onSubmit: SubmitHandler<TBidDecisionSchema> = async (data: TBidDecisionSchema): Promise<void> => {
    const { action, decision, comment } = data;
    // Process @mentions in comments to convert them to the format expected by the backend
    const processedComment = transformMessageForSubmission(comment ?? "", tenderUsers);

    try {
      // Handle comment-only submissions separately from decision submissions
      if (action === DecisionActionEnum.Comment) {
        // Execute comment mutation when user is only adding a comment
        await commentOnDecisionGroupMutation({
          variables: {
            groupId: Number(bidDecisionGroup.id),
            comment: processedComment,
            type: CommentEnum.BidDecision
          }
        });
      } else {
        // For actual decisions (Approve, Flip, or initial Bid/NoBid)
        // Initialize with default action (Approve) and the selected decision
        const newValues = { action: DecisionActionEnum.Approve, decision };

        // If there are existing decisions, determine the appropriate action and decision
        if (hasBidDecisions) {
          const currentDecision = bidDecisionGroup.currentDecision as DecisionEnum;
          if (action === DecisionActionEnum.Approve) {
            // When approving, keep the current decision value
            newValues.decision = currentDecision as DecisionEnum;
          } else if (action === DecisionActionEnum.Flip) {
            // When flipping, change the decision to the opposite of current
            newValues.action = action;
            newValues.decision = currentDecision === DecisionEnum.Bid ? DecisionEnum.Nobid : DecisionEnum.Bid;
          }
        }
        // Otherwise for initial decisions, use the decision value directly from the form

        // Execute the decision mutation with the determined values
        const decideAheadOfTurn = isSkipOption(dropdownValue);
        const commentToSubmit = decideAheadOfTurn
          ? prefixSkipComment(processedComment, awaitingOrganizationName, t)
          : processedComment;

        await submitDecisionMutation({
          variables: {
            input: {
              bidDecisionGroupId: Number(bidDecisionGroupID),
              action: newValues.action,
              comment: commentToSubmit,
              decision: newValues.decision,
              decideAheadOfTurn,
            }
          }
        });
      }
    }
    catch (err) {
      console.error(err);
    }
    finally {
      // Refresh the data to show the latest state after submission
      useRefetch(tenderState);
      // Close the confirmation dialog if it was open
      setDecisionConfirmationOpen(false);
      // Reset the form to its default state for the next interaction
      form.reset(BidDecisionDefaultValues);
      setDropdownValue("");
    }
  };

  /**
   * Handles the change event for the dropdown
   * @param {string} value The selected value from the dropdown
   */
  const handleDropdownChange = (value: string) => {
    setDropdownValue(value);
    form.setValue("decideAheadOfTurn", isSkipOption(value));
    form.trigger("comment");

    if (value === DecisionActionEnum.Comment) {
      form.setValue("action", DecisionActionEnum.Comment);
      form.trigger("action");
      return;
    }

    if (hasBidDecisions) {
      const mappedAction = value === SKIP_APPROVE
        ? DecisionActionEnum.Approve
        : value === SKIP_FLIP
          ? DecisionActionEnum.Flip
          : value;
      form.setValue("action", mappedAction as any);
      form.trigger("action");
      return;
    }

    const mappedDecision = value === SKIP_BID
      ? DecisionEnum.Bid
      : value === SKIP_NOBID
        ? DecisionEnum.Nobid
        : value;
    form.setValue("decision", mappedDecision as any);
    form.setValue("action", "" as any);
    form.trigger("decision");
  };

  // Destructure the comment field registration to override onChange/ref
  const {
    ref: commentRef,
    onChange: commentOnChange,
    ...commentRest
  } = form.register("comment");

  // Compute the filtered list of users based on the current mention query
  const filteredUsers = filterUsersByMention(mentionQuery, tenderUsers);

  return (
    <>
      {userHasPermission ? (
        <Card className={cn("px-2 py-4", hasFinalDecision && "cursor-not-allowed")}>
          <form
            id="tender-decision-form"
            className="flex gap-2 items-start flex-1"
            onSubmit={form.handleSubmit(onSubmit, onInvalid)}
          >
            {/* Decision or Action Dropdown */}
            {<DropdownSelect
              options={
                hasBidDecisions
                  ? options.pending
                  : options.initial
              }
              placeholder={hasBidDecisions ? t("chatinputbar.selectaction") : t("chatinputbar.selectdecision")}
              onChange={handleDropdownChange}
              value={dropdownValue || (
                actionValue === DecisionActionEnum.Comment
                  ? DecisionActionEnum.Comment
                  : hasBidDecisions
                    ? actionValue || ""
                    : decisionValue || ""
              )}
              error={hasBidDecisions ? form.formState.errors.action?.message : form.formState.errors.decision?.message}
              disabled={hasFinalDecision}
              currentLanguage={currentLanguage as string}
              dropdownPosition="top"
            />}

            {/* Comment Input with Mentions */}
            <div className="flex-1 relative">
              <Input
              // Merge the react-hook-form ref with our own ref
                ref={(e) => {
                  commentRef(e);
                  commentInputRef.current = e;
                }}
                value={commentValue}
                onChange={(e) => {
                  commentOnChange(e);
                  handleMentionDetection(e, setMentionQuery);
                }}
                autoComplete="off"
                className={cn(form.formState.errors.comment && "border-red-500 border-2")}
                disabled={hasFinalDecision}
                // Disable keydown events for comment actions
                onKeyDown={actionValue === DecisionActionEnum.Comment ? undefined : (e) => handleOnKeyDown(e, form, setDecisionConfirmationOpen)}
                placeholder={t("chatinputbar.comment")}
                {...commentRest}
              />
              {mentionQuery !== null && filteredUsers.length > 0 && (
                <ChatMentionsPopup
                  filteredUsers={filteredUsers}
                  inputRef={commentInputRef as RefObject<HTMLInputElement | HTMLTextAreaElement>}
                  form={form}
                  formField="comment"
                  setMentionQuery={setMentionQuery}
                />
              )}
            </div>

            {/* Submit Button */}
            <div className="flex">
              <Button
                disabled={hasFinalDecision || !form.formState.isValid || submitLoading || commentLoading}
                type={actionValue === DecisionActionEnum.Comment ? "submit" : "button"}
                onClick={actionValue === DecisionActionEnum.Comment ? undefined : () => setDecisionConfirmationOpen(true)}
                className="gap-2"
              >
                <CircleLoader visible={submitLoading || commentLoading} height={16} width={16} strokeWidth={2} />
                {t("common:buttons.submit")}
              </Button>
            </div>
          </form>
        </Card> ) : (
        <Card className="p-4 flex flex-row gap-2 items-center">
          <TriangleAlert className="w-4 h-4 text-gray-400" />
          <p className="text-sm text-gray-400">{t("chatinputbar.nopermission")}</p>
        </Card>
      )}

      <DecisionConfirmationAlertDialog
        open={decisionConfirmationOpen}
        onOpenChange={setDecisionConfirmationOpen}
        userDecision={decisionValue as any}
        userAction={actionValue as any}
        isSkip={isSkipOption(dropdownValue)}
        awaitingOrganizationName={skipOrganizationLabel}
        loading={submitLoading || commentLoading}
      />
    </>
  );
};

export default ChatInputBar;
